// Typed client for the admin analytics API. The contract lives in the backend
// repo at ANALYTICS_API.md; the gaps between what the panel asks for and what
// the backend can answer are listed in ANALYTICS_TODO.md next to this app.
//
// All reads are admin-only and ride the httpOnly `access_token` cookie
// (credentials: 'include'), exactly like the rest of the admin panel.
//
// Dates are YYYY-MM-DD IST calendar days, ranges inclusive. History runs
// through IST-yesterday — today's numbers only exist after the overnight rollup.
import { BACKEND_URL } from '../config';

// ----------------------------------------------------------------- responses

export interface DayRange {
	from: string;
	to: string;
}

export interface Point {
	day: string;
	value: number;
}

export type Granularity = 'day' | 'week' | 'month';

export interface TimeseriesResponse {
	range: DayRange;
	granularity: Granularity;
	series: Record<string, Point[]>;
}

export interface AuthorityRow {
	authorityId: number;
	name: string;
	organisation: string | null;
	isActive: boolean;
	reportsReceived: number;
}

export interface AuthoritiesResponse {
	range: DayRange;
	rows: AuthorityRow[];
}

// ------------------------------------------------------------------ transport

export class AnalyticsError extends Error {
	status: number;
	constructor(status: number, message: string) {
		super(message);
		this.name = 'AnalyticsError';
		this.status = status;
	}
}

// The backend answers errors as either a Nest JSON envelope ({message}) or a
// bare string, so read the body once as text and only then try to parse it.
async function readError(res: Response): Promise<string> {
	const raw = await res.text().catch(() => '');
	try {
		const parsed = JSON.parse(raw);
		const msg = parsed?.message ?? parsed?.error;
		if (typeof msg === 'string') return msg;
		if (Array.isArray(msg)) return msg.join(', ');
	} catch {
		/* not JSON — fall through to the raw text */
	}
	if (raw) return raw;
	return res.status === 403
		? 'Admin access required.'
		: res.status === 401
			? 'Session expired. Please log in again.'
			: `Request failed (${res.status}).`;
}

async function analyticsGet<T>(
	path: string,
	params: Record<string, string | undefined> = {},
	signal?: AbortSignal
): Promise<T> {
	const qs = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v !== undefined && v !== '') qs.set(k, v);
	}
	const query = qs.toString();
	const res = await fetch(`${BACKEND_URL}/analytics/${path}${query ? `?${query}` : ''}`, {
		method: 'GET',
		credentials: 'include',
		signal,
	});
	if (!res.ok) throw new AnalyticsError(res.status, await readError(res));
	return (await res.json()) as T;
}

/** 1–12 metric keys per call — one request draws a whole section. */
export const fetchTimeseries = (
	metrics: string[],
	range: DayRange,
	opts: { dim?: string; granularity?: Granularity } = {},
	signal?: AbortSignal
) =>
	analyticsGet<TimeseriesResponse>(
		'timeseries',
		{
			metrics: metrics.join(','),
			from: range.from,
			to: range.to,
			dim: opts.dim,
			granularity: opts.granularity,
		},
		signal
	);

/** Only used for the authority / organisation filter lists. */
export const fetchAuthorities = (range: DayRange, signal?: AbortSignal) =>
	analyticsGet<AuthoritiesResponse>('authorities', { from: range.from, to: range.to }, signal);

// ------------------------------------------------------------- metric catalog

export type Unit = 'count' | 'users' | 'seconds';

export interface MetricMeta {
	label: string;
	unit: Unit;
	/** Set when nothing will ever arrive until backend work lands. */
	notTracked?: string;
	/** Set when the pipeline exists but the source isn't sending yet. */
	pending?: string;
}

const AWAITING_APP =
	'The backend records this, but no data arrives until an app release ships the session tracker.';

export const METRICS: Record<string, MetricMeta> = {
	signups: { label: 'Signups', unit: 'count' },
	new_installs: { label: 'New installs', unit: 'count', pending: AWAITING_APP },
	app_sessions: { label: 'App opens', unit: 'count', pending: AWAITING_APP },
	app_time_seconds: { label: 'Total app time', unit: 'seconds', pending: AWAITING_APP },
	dau_app: { label: 'Daily active app users', unit: 'users' },
	wau: { label: 'Weekly active users', unit: 'users' },
	mau: { label: 'Monthly active users', unit: 'users' },
	posts_created: { label: 'Posts', unit: 'count' },
	comments_created: { label: 'Comments', unit: 'count' },
	reactions_created: { label: 'Likes', unit: 'count' },
	shared_post_opens: { label: 'Post shares opened', unit: 'count' },
	reports_created: { label: 'Reports filed', unit: 'count' },
	reports_closed: { label: 'Reports closed', unit: 'count' },
	reports_open: { label: 'Open reports', unit: 'count' },
	web_page_views: { label: 'Website opens', unit: 'count' },
	web_unique_visitors: { label: 'Unique visitors', unit: 'count' },
	authorities_active_web: { label: 'Authorities on the website', unit: 'count' },
	authority_web_visits: { label: 'Website visits', unit: 'count' },
};

export const metricLabel = (key: string) => METRICS[key]?.label ?? key;
export const metricUnit = (key: string): Unit => METRICS[key]?.unit ?? 'count';

// ------------------------------------------------------------- date helpers

/** Today's IST calendar day as YYYY-MM-DD, independent of the browser's zone. */
export const istToday = (): string =>
	new Date(Date.now() + 330 * 60 * 1000).toISOString().slice(0, 10);

/** Shift an IST day string by whole days. */
export const istDayShift = (day: string, delta: number): string => {
	const d = new Date(`${day}T00:00:00Z`);
	d.setUTCDate(d.getUTCDate() + delta);
	return d.toISOString().slice(0, 10);
};

export const istYesterday = (): string => istDayShift(istToday(), -1);

/** Inclusive last-N-days window ending IST-today (matches the backend). */
export const lastNDays = (n: number): DayRange => {
	const to = istToday();
	return { from: istDayShift(to, -(n - 1)), to };
};

export const dayCount = (range: DayRange): number =>
	Math.round(
		(new Date(`${range.to}T00:00:00Z`).getTime() - new Date(`${range.from}T00:00:00Z`).getTime()) /
			86_400_000
	) + 1;

/**
 * Last IST day a bucket covers, clamped to the range. Buckets are labelled by
 * their first day (weeks start Monday), so this is how a weekly/monthly bucket
 * finds the rolling-actives figure that matches its own window.
 */
export const bucketEnd = (day: string, granularity: Granularity, rangeTo: string): string => {
	let end = day;
	if (granularity === 'week') {
		end = istDayShift(day, 6);
	} else if (granularity === 'month') {
		const d = new Date(`${day}T00:00:00Z`);
		d.setUTCMonth(d.getUTCMonth() + 1);
		d.setUTCDate(0); // roll back to the last day of the bucket's own month
		end = d.toISOString().slice(0, 10);
	}
	return end > rangeTo ? rangeTo : end;
};

// ------------------------------------------------------------- formatting

const NBSP = ' '; // thin space, keeps "1.2 K" tight

/** 1,284 / 12.9K / 4.2M — for axis ticks and headline figures. */
export const formatCompact = (n: number): string => {
	const abs = Math.abs(n);
	if (abs >= 1_000_000) return `${trim(n / 1_000_000)}${NBSP}M`;
	if (abs >= 10_000) return `${trim(n / 1_000)}${NBSP}K`;
	return Math.round(n).toLocaleString('en-IN');
};

const trim = (n: number): string => {
	const r = Math.round(n * 10) / 10;
	return Number.isInteger(r) ? String(r) : r.toFixed(1);
};

/** Seconds → "3d 4h" / "5h 12m" / "48s". Used for every app-time figure. */
export const formatDuration = (seconds: number): string => {
	if (!Number.isFinite(seconds) || seconds <= 0) return '0';
	const d = Math.floor(seconds / 86_400);
	const h = Math.floor((seconds % 86_400) / 3_600);
	const m = Math.floor((seconds % 3_600) / 60);
	if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
	if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
	if (m > 0) return `${m}m`;
	return `${Math.round(seconds)}s`;
};

export const formatByUnit = (value: number, unit: Unit): string =>
	unit === 'seconds' ? formatDuration(value) : formatCompact(value);

/** "26 Jul" / "26 Jul 2026" — chart axis + tooltip labels. */
export const formatDay = (day: string, withYear = false): string => {
	const d = new Date(`${day}T00:00:00Z`);
	const mon = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
	return `${d.getUTCDate()} ${mon}${withYear ? ` ${d.getUTCFullYear()}` : ''}`;
};

/** Bucket label for a granularity — weeks/months are labelled by their first day. */
export const formatBucket = (day: string, granularity: Granularity): string => {
	if (granularity === 'month') {
		const d = new Date(`${day}T00:00:00Z`);
		return `${d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })} ${d.getUTCFullYear()}`;
	}
	if (granularity === 'week') return `Week of ${formatDay(day)}`;
	return formatDay(day);
};

/** Short weekday for a day string — "Sun", "Wed". */
export const weekdayOf = (day: string): string =>
	new Date(`${day}T00:00:00Z`).toLocaleString('en-US', { weekday: 'short', timeZone: 'UTC' });

/**
 * Tooltip heading. Daily buckets carry the weekday, which is the whole story
 * behind most of the dips in these charts — it does not go on the axis, where
 * it would just crowd the dates.
 */
export const formatBucketLong = (day: string, granularity: Granularity): string =>
	granularity === 'day'
		? `${weekdayOf(day)}, ${formatDay(day)}`
		: formatBucket(day, granularity);
